"use client"

import AppTitle from "@/components/layout/AppTitle"
import { logger } from "@/lib/logger"
import { initProductionCleanup } from "@/lib/production-cleanup"
import { initFontOptimization } from "@/utils/fontOptimization"
import { Download, X } from "lucide-react"
import { useEffect, useState } from "react"

const SW_PATH = process.env.NEXT_PUBLIC_SW_PATH ?? "/sw.js"
const SW_SCOPE = process.env.NEXT_PUBLIC_SW_SCOPE ?? "/"
const SW_UPDATE_VIA_CACHE =
  process.env.NEXT_PUBLIC_SW_UPDATE_VIA_CACHE ?? "none"
const INSTALL_PROMPT_DELAY_MS = Number(
  process.env.NEXT_PUBLIC_PWA_INSTALL_PROMPT_DELAY_MS ?? "50000"
)
const INSTALL_PROMPT_ENABLED =
  (process.env.NEXT_PUBLIC_PWA_INSTALL_PROMPT_ENABLED ?? "true") === "true"

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed"
    platform: string
  }>
  prompt(): Promise<void>
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)
  const [isClient, setIsClient] = useState(false)

  // Initialize once on mount - don't depend on isStandalone to avoid re-registration
  useEffect(() => {
    // Set client flag
    setIsClient(true)

    // Initialize production cleanup
    initProductionCleanup()

    // Initialize font optimization to prevent preload warnings
    initFontOptimization()

    // Check if app is already installed
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia("(display-mode: standalone)").matches ||
          (navigator as NavigatorWithStandalone).standalone ||
          document.referrer.includes("android-app://")
      )
    }

    checkStandalone()

    // Register service worker only once on mount
    if ("serviceWorker" in navigator) {
      const registrationOptions: RegistrationOptions = {
        scope: SW_SCOPE,
      }

      // updateViaCache is not standardised across all TS lib.dom versions; keep current default
      // and allow overriding via env without strict typing
      if (SW_UPDATE_VIA_CACHE) {
        ;(registrationOptions as Record<string, string>).updateViaCache =
          SW_UPDATE_VIA_CACHE
      }

      navigator.serviceWorker
        .register(SW_PATH, registrationOptions)
        .then((registration) => {
          logger.info(
            `Service Worker registered successfully: ${registration.scope}`
          )

          // Log service worker state for debugging
          if (registration.installing) {
            logger.info("Service Worker installing...")
          } else if (registration.waiting) {
            logger.info("Service Worker waiting...")
          } else if (registration.active) {
            logger.info("Service Worker active")
          }

          // Check for updates
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                logger.info(`Service Worker state changed: ${newWorker.state}`)
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  // New service worker is available
                  logger.info("New service worker available")
                }
              })
            }
          })

          // Periodic check for updates (every hour)
          setInterval(
            () => {
              registration.update().catch((err) => {
                logger.warn("Service Worker update check failed", err)
              })
            },
            60 * 60 * 1000
          )
        })
        .catch((error) => {
          logger.error("Service Worker registration failed", error)
          // Log detailed error information for debugging
          if (error instanceof Error) {
            console.error("SW Registration Error Details:", {
              message: error.message,
              stack: error.stack,
              swPath: SW_PATH,
              scope: SW_SCOPE,
            })
          }
        })
    }
  }, []) // Empty dependency array - run only once on mount

  // Handle install prompt separately - depends on isStandalone
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Only prevent default if we actually plan to show our custom prompt
      // This prevents the browser warning about preventDefault without prompt()
      if (
        INSTALL_PROMPT_ENABLED &&
        !isStandalone &&
        !sessionStorage.getItem("installPromptDismissed")
      ) {
        e.preventDefault()
        setDeferredPrompt(e as BeforeInstallPromptEvent)
        // Show install prompt after a delay if not standalone
        setTimeout(() => setShowInstallPrompt(true), INSTALL_PROMPT_DELAY_MS)
      }
      // If standalone or already dismissed, let browser handle normally
      // This prevents the "preventDefault called but prompt() not called" warning
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)

    // Handle app installed
    const handleAppInstalled = () => {
      logger.info("PWA was installed")
      setShowInstallPrompt(false)
      setDeferredPrompt(null)
    }

    window.addEventListener("appinstalled", handleAppInstalled)

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      )
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [isStandalone]) // Only re-setup event listeners when isStandalone changes

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === "accepted") {
      logger.info("User accepted the install prompt")
    }

    setDeferredPrompt(null)
    setShowInstallPrompt(false)
  }

  const handleDismiss = () => {
    setShowInstallPrompt(false)
    // Don't show again for this session (only on client)
    if (typeof window !== "undefined") {
      sessionStorage.setItem("installPromptDismissed", "true")
    }
  }

  // Don't show if already dismissed this session (only check on client)
  const dismissed =
    isClient && typeof window !== "undefined"
      ? sessionStorage.getItem("installPromptDismissed")
      : null

  return (
    <>
      {children}

      {/* Install prompt - only render on client */}
      {isClient &&
        showInstallPrompt &&
        !isStandalone &&
        !dismissed &&
        deferredPrompt && (
          <div className="fixed right-4 bottom-4 left-4 z-50 block md:right-4 md:left-auto md:w-96">
            <div className="bg-background border-border rounded-lg border p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <Download className="text-primary h-5 w-5" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 -mt-1">
                    <h3 className="text-foreground font-semibold">Install</h3>
                    <AppTitle showLabel={false} size="sm" />
                  </div>
                  <p className="my-2 text-xs">for a quicker access</p>

                  <div className="flex gap-2">
                    <button
                      onClick={handleInstallClick}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 py-1.5 text-sm transition-colors"
                    >
                      Install
                    </button>
                    <button
                      onClick={handleDismiss}
                      className="text-muted-foreground hover:text-foreground px-3 py-1.5 text-sm transition-colors"
                    >
                      Not now
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleDismiss}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  )
}
